import { useState, useEffect } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend } from 'chart.js'
import { getVentesJournalieres, CONCESSIONS, MOIS_FR } from '../lib/supabase'
import './Dashboard.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend)

function fmt(n) { return n?.toLocaleString('fr-CA') ?? '—' }

function getDebutFin(periode) {
  const now = new Date()
  const annee = now.getFullYear()
  const mois = now.getMonth()

  if (periode === 'semaine_courante') {
    const day = now.getDay()
    const diff = day === 0 ? -6 : 1 - day
    const lundi = new Date(now)
    lundi.setDate(now.getDate() + diff)
    const dimanche = new Date(lundi)
    dimanche.setDate(lundi.getDate() + 6)
    return {
      debut: lundi.toISOString().split('T')[0],
      fin: dimanche.toISOString().split('T')[0],
      label: `Semaine du ${lundi.toLocaleDateString('fr-CA', {day:'numeric', month:'long'})} au ${dimanche.toLocaleDateString('fr-CA', {day:'numeric', month:'long', year:'numeric'})}`,
      anneeRef: annee, moisRef: mois + 1
    }
  }
  if (periode === 'semaine_precedente') {
    const day = now.getDay()
    const diff = day === 0 ? -6 : 1 - day
    const lundiCourant = new Date(now)
    lundiCourant.setDate(now.getDate() + diff)
    const lundiPrec = new Date(lundiCourant)
    lundiPrec.setDate(lundiCourant.getDate() - 7)
    const dimPrec = new Date(lundiPrec)
    dimPrec.setDate(lundiPrec.getDate() + 6)
    return {
      debut: lundiPrec.toISOString().split('T')[0],
      fin: dimPrec.toISOString().split('T')[0],
      label: `Semaine du ${lundiPrec.toLocaleDateString('fr-CA', {day:'numeric', month:'long'})} au ${dimPrec.toLocaleDateString('fr-CA', {day:'numeric', month:'long', year:'numeric'})}`,
      anneeRef: lundiPrec.getFullYear(), moisRef: lundiPrec.getMonth() + 1
    }
  }
  if (periode === 'mois_courant') {
    const debut = new Date(annee, mois, 1)
    const fin = new Date(annee, mois + 1, 0)
    return {
      debut: debut.toISOString().split('T')[0],
      fin: fin.toISOString().split('T')[0],
      label: `${MOIS_FR[mois + 1]} ${annee}`,
      anneeRef: annee, moisRef: mois + 1
    }
  }
  if (periode === 'mois_precedent') {
    const debut = new Date(annee, mois - 1, 1)
    const fin = new Date(annee, mois, 0)
    const moisPrec = mois === 0 ? 12 : mois
    const anneePrec = mois === 0 ? annee - 1 : annee
    return {
      debut: debut.toISOString().split('T')[0],
      fin: fin.toISOString().split('T')[0],
      label: `${MOIS_FR[moisPrec]} ${anneePrec}`,
      anneeRef: anneePrec, moisRef: moisPrec
    }
  }
  if (periode === 'annee_a_date') {
    return {
      debut: `${annee}-01-01`,
      fin: now.toISOString().split('T')[0],
      label: `Année à date — ${annee}`,
      anneeRef: annee, moisRef: mois + 1
    }
  }
}

export default function Dashboard() {
  const now = new Date()
  const annee = now.getFullYear()
  const mois = now.getMonth() + 1

  const [periode, setPeriode] = useState('semaine_courante')
  const [totPeriode, setTotPeriode] = useState(null)
  const [totPeriodePrec, setTotPeriodePrec] = useState(null)
  const [tendanceCourante, setTendanceCourante] = useState({})
  const [tendancePrec, setTendancePrec] = useState({})
  const [loading, setLoading] = useState(true)

  const periodeInfo = getDebutFin(periode)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const { debut, fin, anneeRef, moisRef } = getDebutFin(periode)

        // Même période année précédente
        let debutPrecEffectif, finPrecEffectif

        if (periode === 'semaine_courante' || periode === 'semaine_precedente') {
          const d1 = new Date(debut)
          const d2 = new Date(fin)
          d1.setFullYear(d1.getFullYear() - 1)
          d2.setFullYear(d2.getFullYear() - 1)
          debutPrecEffectif = d1.toISOString().split('T')[0]
          finPrecEffectif   = d2.toISOString().split('T')[0]
        } else if (periode === 'annee_a_date') {
          const finAnneePrec = new Date(now)
          finAnneePrec.setFullYear(finAnneePrec.getFullYear() - 1)
          debutPrecEffectif = `${anneeRef - 1}-01-01`
          finPrecEffectif   = finAnneePrec.toISOString().split('T')[0]
        } else {
          const anneeRefPrec = anneeRef - 1
          const lastDay = new Date(anneeRefPrec, moisRef, 0).getDate()
          debutPrecEffectif = `${anneeRefPrec}-${String(moisRef).padStart(2,'0')}-01`
          finPrecEffectif   = `${anneeRefPrec}-${String(moisRef).padStart(2,'0')}-${lastDay}`
        }

        // 12 derniers mois complets (pour graphique tendance)
        const dateDebut12 = new Date(now)
        dateDebut12.setMonth(dateDebut12.getMonth() - 12)
        dateDebut12.setDate(1)
        const debut12 = dateDebut12.toISOString().split('T')[0]
        const fin12   = new Date(annee, mois - 1, 0).toISOString().split('T')[0]

        const dateDebut12Prec = new Date(dateDebut12)
        dateDebut12Prec.setFullYear(dateDebut12Prec.getFullYear() - 1)
        const debut12Prec = dateDebut12Prec.toISOString().split('T')[0]
        const fin12Prec   = new Date(annee - 1, mois - 1, 0).toISOString().split('T')[0]

        const [ventesData, ventesPrecData, tendance12, tendance12Prec] = await Promise.all([
          getVentesJournalieres(debut, fin),
          getVentesJournalieres(debutPrecEffectif, finPrecEffectif),
          getVentesJournalieres(debut12, fin12),
          getVentesJournalieres(debut12Prec, fin12Prec),
        ])

        function agregerConc(ventes) {
          const totaux = {}
          CONCESSIONS.forEach(c => { totaux[c.id] = { neuf: 0, occasion: 0 } })
          ventes.forEach(r => {
            if (totaux[r.concession]) {
              totaux[r.concession].neuf += r.neuf
              totaux[r.concession].occasion += r.occasion
            }
          })
          return totaux
        }

        function agregerMois(ventes) {
          const map = {}
          ventes.forEach(r => {
            const key = r.date.substring(0, 7)
            if (!map[key]) map[key] = 0
            map[key] += r.neuf + r.occasion
          })
          return map
        }

        setTotPeriode(agregerConc(ventesData))
        setTotPeriodePrec(agregerConc(ventesPrecData))
        console.log('debut12:', debut12, 'fin12:', fin12)
        console.log('tendance12 raw:', tendance12)
        console.log('tendance12 agrégé:', agregerMois(tendance12))
        setTendanceCourante(agregerMois(tendance12))
        setTendancePrec(agregerMois(tendance12Prec))

      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [periode])

  const totalNeuf     = totPeriode     ? CONCESSIONS.reduce((s, c) => s + (totPeriode[c.id]?.neuf || 0), 0) : 0
  const totalOcc      = totPeriode     ? CONCESSIONS.reduce((s, c) => s + (totPeriode[c.id]?.occasion || 0), 0) : 0
  const totalPrecNeuf = totPeriodePrec ? CONCESSIONS.reduce((s, c) => s + (totPeriodePrec[c.id]?.neuf || 0), 0) : 0
  const totalPrecOcc  = totPeriodePrec ? CONCESSIONS.reduce((s, c) => s + (totPeriodePrec[c.id]?.occasion || 0), 0) : 0

  const { anneeRef, moisRef } = periodeInfo

  // 12 derniers mois complets
  const derniersMoisComplets = []
  for (let i = 12; i >= 1; i--) {
    const d = new Date(annee, mois - 1 - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`
    const keyPrec = `${d.getFullYear() - 1}-${String(d.getMonth() + 1).padStart(2,'0')}`
    derniersMoisComplets.push({
      label: `${MOIS_FR[d.getMonth() + 1].substring(0,3)} ${String(d.getFullYear()).substring(2)}`,
      key, keyPrec
    })
  }

  const moisLabels  = derniersMoisComplets.map(d => d.label)
  const dataCourant = derniersMoisComplets.map(d => tendanceCourante[d.key] || 0)
  const dataPrec    = derniersMoisComplets.map(d => tendancePrec[d.keyPrec] || 0)

  // Graphique barres empilées
  const concLabels = CONCESSIONS.map(c => c.label.replace('Subaru ','').replace('Hyundai ','').replace('Honda ',''))

  const barData = {
    labels: concLabels,
    datasets: [
      { label: `Neufs — période`, data: CONCESSIONS.map(c => totPeriode?.[c.id]?.neuf || 0), backgroundColor: '#185FA5', borderRadius: 0, stack: 'courant' },
      { label: `Occasion — période`, data: CONCESSIONS.map(c => totPeriode?.[c.id]?.occasion || 0), backgroundColor: '#3B6D11', borderRadius: 3, stack: 'courant' },
      { label: `Neufs — an préc.`, data: CONCESSIONS.map(c => totPeriodePrec?.[c.id]?.neuf || 0), backgroundColor: '#7AAFD4', borderRadius: 0, stack: 'precedent' },
      { label: `Occasion — an préc.`, data: CONCESSIONS.map(c => totPeriodePrec?.[c.id]?.occasion || 0), backgroundColor: '#8BBF7A', borderRadius: 3, stack: 'precedent' },
    ]
  }

  const barOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12 } },
      tooltip: { callbacks: { footer: (items) => `Total : ${items.reduce((s,i) => s+i.raw, 0)}` } }
    },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#999', font: { size: 10 } } },
      y: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#999', font: { size: 10 } } },
    }
  }

  const lineData = {
    labels: moisLabels,
    datasets: [
      { label: 'Année courante', data: dataCourant, borderColor: '#C8102E', backgroundColor: 'rgba(200,16,46,0.08)', tension: 0.3, pointRadius: 4, fill: true },
      { label: 'Année précédente', data: dataPrec, borderColor: '#bbb', backgroundColor: 'transparent', tension: 0.3, pointRadius: 3, borderDash: [4,3] }
    ]
  }

  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12 } } },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#999', font: { size: 11 } } },
      y: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#999', font: { size: 11 } } }
    }
  }

  const colonneLabel = {
    semaine_courante: 'Total semaine', semaine_precedente: 'Total semaine',
    mois_courant: 'Total mois', mois_precedent: 'Total mois', annee_a_date: 'Total année à date',
  }[periode]

  const labelPrec = periode === 'annee_a_date'
    ? `Même période ${anneeRef - 1}`
    : `${MOIS_FR[moisRef]} ${anneeRef - 1}`

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1>Ventes — {periodeInfo.label}</h1>
          <p>Données en temps réel · Rapport hebdo envoyé chaque lundi matin</p>
        </div>
        <div className="live-badge"><span className="live-dot" />En direct</div>
      </div>

      <div className="periode-selector">
        <label>Période :</label>
        <select value={periode} onChange={e => setPeriode(e.target.value)}>
          <option value="semaine_courante">Semaine courante</option>
          <option value="semaine_precedente">Semaine précédente</option>
          <option value="mois_courant">Mois en cours</option>
          <option value="mois_precedent">Mois précédent</option>
          <option value="annee_a_date">Année à date</option>
        </select>
      </div>

      {loading ? <div className="loading">Chargement…</div> : (
        <>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Neufs</div>
              <div className="metric-val">{fmt(totalNeuf)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Occasion</div>
              <div className="metric-val">{fmt(totalOcc)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total</div>
              <div className="metric-val">{fmt(totalNeuf + totalOcc)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">{labelPrec}</div>
              <div className="metric-val">{fmt(totalPrecNeuf + totalPrecOcc)}</div>
              <div className="metric-sub">{totalPrecNeuf} neufs · {totalPrecOcc} occ.</div>
            </div>
          </div>

          <div className="section-title">Détail par concession</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Concession</th>
                  <th>Neufs</th>
                  <th>Occasion</th>
                  <th>{colonneLabel}</th>
                  <th>{labelPrec}</th>
                </tr>
              </thead>
              <tbody>
                {CONCESSIONS.map(c => {
                  const p = totPeriode?.[c.id]     || { neuf: 0, occasion: 0 }
                  const m = totPeriodePrec?.[c.id] || { neuf: 0, occasion: 0 }
                  return (
                    <tr key={c.id}>
                      <td>
                        <span className="conc-name">{c.label}</span>
                        <span className={`brand-tag tag-${c.marque.toLowerCase()}`}>{c.marque}</span>
                      </td>
                      <td className="num">{p.neuf}</td>
                      <td className="num">{p.occasion}</td>
                      <td className="num bold">{p.neuf + p.occasion}</td>
                      <td className="num">{m.neuf + m.occasion}</td>
                    </tr>
                  )
                })}
                <tr className="total-row">
                  <td>Total Groupe</td>
                  <td className="num">{totalNeuf}</td>
                  <td className="num">{totalOcc}</td>
                  <td className="num bold">{totalNeuf + totalOcc}</td>
                  <td className="num">{totalPrecNeuf + totalPrecOcc}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="charts-row">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Période sélectionnée vs même période an précédent</h3>
              </div>
              <div style={{height: 260}}>
                <Bar data={barData} options={barOpts} />
              </div>
            </div>
            <div className="chart-card">
              <div className="chart-header">
                <h3>12 derniers mois complets</h3>
              </div>
              <div style={{height: 260}}>
                <Line data={lineData} options={lineOpts} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
